/**
 * ClientEmailSummaryCard — card fissa sopra la timeline del lead con il
 * riassunto IA dello storico email (Gmail + Outlook, dedup cross-casella).
 * Stile Notion-like coerente con EmailNoteCard (barra accento blu, icona Sparkles).
 *
 * Consuma i tipi di `clientMailService.ts` (Task 9) e viene alimentata dal hook
 * di Task 10 nel componente genitore (lead detail timeline).
 */

import React from 'react';
import { Sparkles, RefreshCw, Mail } from 'lucide-react';
import type { ClientMailSummary, ClientMailStats } from '../../lib/services/clientMailService';
import { CARD_HEAD_CLS, ICON_CHIP_CLS, KICKER_CLS } from '../shared/timeline/cardStyles';

interface Props {
  summary: ClientMailSummary | null;
  stats: ClientMailStats | null;
  newEmailCount: number;
  generating: boolean;
  onRegenerate: () => void;
  t: any;
  formatDate: (d: string) => string;
}

export const ClientEmailSummaryCard: React.FC<Props> = ({
  summary, stats, newEmailCount, generating, onRegenerate, t, formatDate,
}) => {
  const tr = t.emailTimeline ?? {};
  if (!summary && !generating) return null;
  return (
    // NON è una card (scelta del 12 ago): blocco piatto sopra la cronologia, senza bordo
    // né sfondo, chiuso da un divisore sottile. Testa con iconcina gradiente + kicker.
    <div className="mb-3 pb-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
      <div className="min-w-0">
        <div className={CARD_HEAD_CLS}>
          <span className={ICON_CHIP_CLS}>
            <Sparkles size={12} strokeWidth={1.5} />
          </span>
          <span className={KICKER_CLS}>{tr.summaryTitle}</span>
          <span className="ml-auto flex items-center gap-3 whitespace-nowrap">
            {newEmailCount > 0 && !generating && (
              <span className="flex items-center gap-1 text-[11px] text-[#9B9A97] dark:text-white/40">
                <Mail size={12} />
                {(tr.newEmails || '{count}').replace('{count}', String(newEmailCount))}
              </span>
            )}
            <button
              onClick={onRegenerate}
              disabled={generating}
              className="flex items-center gap-1 text-[11px] text-[#2383E2] hover:underline disabled:opacity-50"
            >
              <RefreshCw size={12} className={generating ? 'animate-spin' : ''} />
              {tr.regenerate}
            </button>
          </span>
        </div>
        {generating && !summary ? (
          <p className="mt-1.5 text-[13px] italic text-[#787774] dark:text-white/50">{tr.generating}</p>
        ) : (
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[#37352F] dark:text-white/90">
            {summary?.summary}
          </p>
        )}
        {summary && stats && (
          <div className="mt-1.5 text-[11px] text-[#9B9A97] dark:text-white/40">
            {tr.updatedAt} {formatDate(summary.generated_at)}
            {stats.last_interaction_at && <> · {tr.lastInteraction} {formatDate(stats.last_interaction_at)}</>}
            {' · '}
            {(tr.threadsCount || '')
              .replace('{threads}', String(stats.total_threads))
              .replace('{messages}', String(stats.total_messages))}
          </div>
        )}
      </div>
    </div>
  );
};
